import React from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { useTheme } from '@veneer/theme';
import { doubleFocusRing } from '../utils/focus_ring';
import hideVisually from 'polished/lib/mixins/hideVisually';

type RadioButtonStyle = {
  root: {
    base: SerializedStyles;
    checked: SerializedStyles;
    disabled: SerializedStyles;
  };
  interactive: SerializedStyles;
  decorative: {
    base: SerializedStyles;
    checked: SerializedStyles;
    disabled: SerializedStyles;
  };
  label: SerializedStyles;
  hc: {
    root: {
      base: SerializedStyles;
      disabled: SerializedStyles;
    };
  };
};

const useStyles = (): RadioButtonStyle => {
  const theme = useTheme();

  return React.useMemo(
    () => ({
      root: {
        base: css({
          //to achieve 24px target size on HD
          padding: `calc(calc(24px - ${theme.orbitalSix}) / 2 )`,
          margin: `calc(calc(${theme.orbitalSix} - 24px) / 2 )`,

          gap: theme.orbitalThree,
          color: theme.color.foreground.default,
          cursor: 'pointer',
          display: 'flex',
          wordBreak: 'break-word',
          '.vn-radio-button__icon': {
            backgroundColor: theme.color.background.container.default,
          },
          '&:hover > .vn-radio-button__icon': {
            backgroundColor: theme.color.background.container.hover,
            borderColor: theme.color.neutral.base.hover,
          },
          '&:active > .vn-radio-button__icon': {
            backgroundColor: theme.color.background.container.active,
            borderColor: theme.color.neutral.base.active,
          },
        }),

        checked: css({
          '&:hover > .vn-radio-button__icon': {
            backgroundColor: theme.color.primary.foreground.hover,
            borderColor: theme.color.primary.base.hover,
          },
          '&:active > .vn-radio-button__icon': {
            backgroundColor: theme.color.primary.foreground.active,
            borderColor: theme.color.primary.base.active,
          },
        }),
        disabled: css({
          pointerEvents: 'none',
          color: theme.color.foreground.disabled,
          cursor: 'default',

          '> .vn-radio-button__icon': {
            backgroundColor: theme.color.background.disabled,
          },
        }),
      },

      decorative: {
        base: css({
          border: `1px solid ${theme.color.neutral.base.default}`,
          borderRadius: '100%',
          boxSizing: 'border-box',
          height: theme.orbitalSix,
          width: theme.orbitalSix,
          minWidth: theme.orbitalSix,
          minHeight: theme.orbitalSix,
          transition: 'background-color 0.2s ease',
        }),

        checked: css({
          borderWidth: '6px',
          transition: ' background-color 0.2s ease, border-color 0.2s ease',
          backgroundColor: theme.color.primary.foreground.default,
          borderColor: theme.color.primary.base.default,
        }),

        disabled: css({
          borderColor: theme.color.foreground.disabled,
        }),
      },

      interactive: css({
        ...hideVisually(),
        clip: 'unset',
        zIndex: 1,
        opacity: 0,

        ...doubleFocusRing({
          theme,
          shadowSize: 2,
          extension: '+ .vn-radio-button__icon',
        }),
      }),

      label: css({
        transition: 'color 0.2s ease',
        alignContent: 'center',
        ...theme.typography.labelRegular,
      }),

      hc: {
        root: {
          base: css({
            color: theme.color.neutral.base.default,
            '&:hover': {
              color: theme.color.neutral.base.hover,
            },
            '&:active': {
              color: theme.color.neutral.base.active,
            },
          }),
          disabled: css({
            color: theme.color.foreground.disabled,
          }),
        },
      },
    }),
    [theme]
  );
};

export default useStyles;
